import { Component } from 'react';
import { ConverterLayout, layoutStyles } from '../converter-layout/converter-layout';

type LibState =
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'ready'; opentype: any }
    | { kind: 'error'; message: string };

type FontFormat = 'ttf' | 'otf' | 'woff' | 'woff2' | 'eot';
const FORMATS: FontFormat[] = ['ttf', 'otf', 'woff', 'woff2', 'eot'];

interface State {
    fromFormat: FontFormat;
    toFormat: FontFormat;
    fontBuffer: ArrayBuffer | null;
    fontName: string;
    outputUrl: string | null;
    outputSize: number;
    lib: LibState;
    error: string | null;
    busy: boolean;
}

function parseSlug(slug: string | undefined): { from: FontFormat; to: FontFormat } | null {
    if (!slug) return null;
    // Format hub ({ext}-converter): from = the hub's format, to = a sensible default.
    const hub = slug.match(/^([a-z0-9]+)-converter$/);
    if (hub) {
        const from = hub[1] as FontFormat;
        if (!FORMATS.includes(from)) return null;
        return { from, to: (from === 'woff2' ? 'ttf' : 'woff2') as FontFormat };
    }
    const m = slug.match(/^convert-([a-z0-9]+)-to-([a-z0-9]+)$/);
    if (!m) return null;
    const f = m[1] as FontFormat, t = m[2] as FontFormat;
    if (!FORMATS.includes(f) || !FORMATS.includes(t)) return null;
    return { from: f, to: t };
}

export default class FontConverterComponent extends Component<{ feature?: string }, State> {
    constructor(props: { feature?: string }) {
        super(props);
        const parsed = parseSlug(props.feature) || { from: 'ttf', to: 'woff2' };
        this.state = {
            fromFormat: parsed.from,
            toFormat: parsed.to,
            fontBuffer: null,
            fontName: '',
            outputUrl: null,
            outputSize: 0,
            lib: { kind: 'idle' },
            error: null,
            busy: false,
        };
    }

    componentWillUnmount() {
        if (this.state.outputUrl) URL.revokeObjectURL(this.state.outputUrl);
    }

    private async ensureLib(): Promise<any | null> {
        if (this.state.lib.kind === 'ready') return this.state.lib.opentype;
        this.setState({ lib: { kind: 'loading' } });
        try {
            const mod = await import('opentype.js');
            const opentype = (mod as any).default || mod;
            this.setState({ lib: { kind: 'ready', opentype } });
            return opentype;
        } catch (e: any) {
            this.setState({ lib: { kind: 'error', message: e.message || 'Failed to load opentype.js' } });
            return null;
        }
    }

    onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const reader = new FileReader();
        reader.onload = () => {
            this.setState({ fontBuffer: reader.result as ArrayBuffer, fontName: f.name, outputUrl: null, error: null });
        };
        reader.readAsArrayBuffer(f);
    };

    convert = async () => {
        if (!this.state.fontBuffer) return;
        this.setState({ busy: true });
        const opentype = await this.ensureLib();
        if (!opentype) { this.setState({ busy: false }); return; }
        try {
            const font = opentype.parse(this.state.fontBuffer);
            const { toFormat } = this.state;
            const ttfBuffer = font.toArrayBuffer();
            let outputBuffer: ArrayBuffer | Uint8Array = ttfBuffer;
            let mimeType = 'application/octet-stream';

            if (toFormat === 'ttf') {
                mimeType = 'font/ttf';
            } else if (toFormat === 'otf') {
                mimeType = 'font/otf';
            } else if (toFormat === 'woff') {
                const mod: any = await import('ttf2woff');
                const ttf2woff = mod.default || mod;
                outputBuffer = ttf2woff(new Uint8Array(ttfBuffer));
                mimeType = 'font/woff';
            } else if (toFormat === 'woff2') {
                const mod: any = await import('wawoff2');
                const compress = mod.compress || mod.default?.compress;
                outputBuffer = await compress(new Uint8Array(ttfBuffer));
                mimeType = 'font/woff2';
            } else if (toFormat === 'eot') {
                const mod: any = await import('ttf2eot');
                const ttf2eot = mod.default || mod;
                outputBuffer = ttf2eot(new Uint8Array(ttfBuffer));
                mimeType = 'application/vnd.ms-fontobject';
            }
            const blob = new Blob([outputBuffer as any], { type: mimeType });
            const url = URL.createObjectURL(blob);
            this.setState({ outputUrl: url, outputSize: blob.size, error: null, busy: false });
        } catch (e: any) {
            this.setState({ error: e.message || 'Conversion failed', busy: false });
        }
    };

    download = () => {
        if (!this.state.outputUrl) return;
        const a = document.createElement('a');
        a.href = this.state.outputUrl;
        a.download = `output.${this.state.toFormat}`;
        a.click();
    };

    render() {
        const { fromFormat, toFormat, fontBuffer, fontName, outputUrl, outputSize, lib, error, busy } = this.state;
        const inputPanel = (
            <div className={layoutStyles.area}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: '0.75rem', minHeight: 320 }}>
                {fontBuffer ? <>
                    <div style={{ fontSize: '2.5rem' }}>🅰</div>
                    <div style={{ fontWeight: 600, color: '#1e293b' }}>{fontName}</div>
                    <div style={{ fontSize: '0.85rem', color: '#64748b' }}>{(fontBuffer.byteLength / 1024).toFixed(1)} KB · {fromFormat.toUpperCase()}</div>
                </> : <>
                    <div style={{ fontSize: '2.5rem', opacity: 0.4 }}>🅰</div>
                    <div style={{ color: '#64748b' }}>Choose a {fromFormat.toUpperCase()} font file to convert</div>
                </>}
            </div>
        );

        const outputPanel = (
            <div className={layoutStyles.area}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: '0.75rem', minHeight: 320 }}>
                {outputUrl ? <>
                    <div style={{ fontSize: '2.5rem' }}>🅰</div>
                    <div style={{ fontWeight: 600, color: '#065f46' }}>Converted</div>
                    <div style={{ fontSize: '0.85rem', color: '#64748b' }}>{(outputSize / 1024).toFixed(1)} KB · {toFormat.toUpperCase()}</div>
                </> : <>
                    <div style={{ fontSize: '2.5rem', opacity: 0.4 }}>🅰</div>
                    <div style={{ color: '#64748b' }}>Output will appear here after conversion</div>
                </>}
            </div>
        );

        return (
            <ConverterLayout
                toolbar={<>
                    <div className={layoutStyles.formatField}>
                        <label htmlFor="font-from">From format</label>
                        <select id="font-from" value={fromFormat}
                            onChange={e => this.setState({ fromFormat: e.target.value as FontFormat })}>
                            {FORMATS.map(f => <option key={f} value={f}>{f.toUpperCase()}</option>)}
                        </select>
                    </div>
                    <div className={layoutStyles.formatField}>
                        <label htmlFor="font-to">To format</label>
                        <select id="font-to" value={toFormat}
                            onChange={e => this.setState({ toFormat: e.target.value as FontFormat })}>
                            {FORMATS.map(f => <option key={f} value={f}>{f.toUpperCase()}</option>)}
                        </select>
                    </div>
                </>}

                inputLabel={`Input (${fromFormat.toUpperCase()})`}
                inputMeta={fontBuffer ? `${(fontBuffer.byteLength / 1024).toFixed(1)} KB` : undefined}
                inputArea={inputPanel}
                inputActions={
                    <label className={layoutStyles.fileDrop}>
                        📂 Choose font file
                        <input type="file" accept=".ttf,.otf,.woff,.woff2,.eot,font/*" onChange={this.onFileChange} />
                    </label>
                }

                outputLabel={`Output (${toFormat.toUpperCase()})`}
                outputMeta={outputUrl ? `${(outputSize / 1024).toFixed(1)} KB` : undefined}
                outputArea={outputPanel}
                outputActions={
                    <button className={`${layoutStyles.iconBtn} ${layoutStyles.iconBtnPrimary}`}
                        onClick={this.download} disabled={!outputUrl}>
                        ⬇ Download .{toFormat}
                    </button>
                }

                error={error || (lib.kind === 'error' ? (lib as any).message : null)}

                primaryAction={
                    <button className={layoutStyles.primaryBtn}
                        onClick={this.convert}
                        disabled={!fontBuffer || busy || lib.kind === 'loading'}>
                        {busy ? 'Converting…' : lib.kind === 'loading' ? 'Loading opentype.js…' : 'Convert →'}
                    </button>
                }
            />
        );
    }
}
